import { useState } from 'react';
import { Direction, NavBtn } from 'shared/components/calendar/NavBtn';

import { monthNames } from 'shared/util/calendar';
import { Calendar, type CalendarEvent, type CalendarProps, type DateItem } from '@/components/calendar/Calendar';
import { type AvailabilityPerDate, EventTypes } from '@/domain';
import { useCalendarDates } from '@/hooks/useCalendarDates';
import { useCalendarHandleDateSelect } from '@/hooks/useCalendarHandleDateSelect';
import { useCalendarEvents } from './hooks/useCalendarEvents';

export type BasicCalendarProps = Partial<CalendarProps> & {
    availabilityPerDate?: AvailabilityPerDate[];
    calendarDayPropMiddleware?: (dateItem: DateItem) => Record<string, string | boolean>;
    className?: string;
    dayDataByMonth?: Record<string, Record<string, string>>;
    events: CalendarEvent[];
    offsetStartDay?: number;
    onDaySelect?: (dateItem: DateItem) => void;
    onMonthChange?: (date: Date) => void;
    onReset: () => void;
    onSelectChange?: (dates: { startDate: Date | null; endDate: Date | null }) => void;
    selectedDates: {
        to: Date | null;
        from: Date | null;
    };
    title?: string;
    today?: Date;
};

export const BasicCalendar = ({
    availabilityPerDate,
    calendarDayPropMiddleware,
    className,
    dayDataByMonth = {},
    events = [],
    offsetStartDay = 1,
    onDaySelect = () => undefined,
    onMonthChange = () => undefined,
    onReset,
    onSelectChange = (_) => undefined,
    selectedDates,
    title,
    today = new Date(),
}: BasicCalendarProps) => {
    const lastDateWithAvailabilityData =
        availabilityPerDate !== undefined && availabilityPerDate?.length > 0
            ? new Date(availabilityPerDate[availabilityPerDate.length - 1].date)
            : today;
    const validMonthRange: [number, number] = [
        0,
        (lastDateWithAvailabilityData?.getFullYear() - today.getFullYear()) * 12 +
            lastDateWithAvailabilityData?.getMonth() -
            today.getMonth(),
    ];

    const selectedStartDate = selectedDates?.from;
    const selectedEndDate = selectedDates?.to;
    const todayMonthDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultSelectedMonthDate = selectedStartDate
        ? new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), 1)
        : todayMonthDate;
    const [activeMonthDate, setActiveMonthDate] = useState<Date>(defaultSelectedMonthDate);
    const { updatedEvents } = useCalendarEvents({
        activeMonthDate,
        availabilityPerDate,
        events,
        selectedEndDate,
        selectedStartDate,
        today,
    });
    const {
        disallowedFollowingMonthDate,
        disallowedPreviousMonthDate,
        monthIndex,
        selectedNextMonthDate,
        selectedPreviousMonthDate,
        year,
    } = useCalendarDates({
        events,
        selectedDates,
        activeMonthDate,
        today,
        validMonthRange,
    });
    const { handleDaySelect } = useCalendarHandleDateSelect({
        availabilityPerDate,
        events,
        onSelectChange,
        onDaySelect,
        selectedEndDate,
        selectedStartDate,
        today,
    });
    const isResetButtonDisabled = !(selectedStartDate || todayMonthDate.getTime() < activeMonthDate.getTime());

    const shiftDisplayMonth = (monthDifference: number) => {
        const newDate = new Date(activeMonthDate.getFullYear(), activeMonthDate.getMonth() + monthDifference, 1);
        setActiveMonthDate(newDate);
        typeof onMonthChange === 'function' && onMonthChange(newDate);
    };

    const handleReset = () => {
        setActiveMonthDate(todayMonthDate);
        onReset();
    };

    // Set disabled on day button elements if in past or is a booking
    const createCalendarDayProps = (dateItem: DateItem): Record<string, string | boolean> => {
        const isDaySelected = Boolean(
            dateItem.activeEvents?.some((event) => (event.status === 'start' || event.status === 'end') && event.type === 'select'),
        );
        const { isExpired } = dateItem.states ?? {};
        const isMinimumRentalDuration = Boolean(dateItem.activeEvents?.some((item) => item.type === EventTypes.MINIMUM_RENTAL_DURATION));
        const isMaximumRentalDuration = Boolean(dateItem.activeEvents?.some((item) => item.type === EventTypes.MAXIMUM_RENTAL_DURATION));
        const isInvalidCheckinDay = Boolean(dateItem.activeEvents?.some((item) => item.type === EventTypes.INVALID_CHECKIN_DAY));
        const isInvalidCheckoutDay = Boolean(dateItem.activeEvents?.some((item) => item.type === EventTypes.INVALID_CHECKOUT_DAY));
        const isPreviousDayDisabled = Boolean(
            selectedStartDate && !selectedEndDate && dateItem.date.getTime() < selectedStartDate.getTime(),
        );
        const { minDuration, maxDuration } =
            availabilityPerDate?.find((rule) => {
                const ruleDate = new Date(rule.date);
                return (
                    ruleDate.getFullYear() === selectedStartDate?.getFullYear() &&
                    ruleDate.getMonth() === selectedStartDate?.getMonth() &&
                    ruleDate.getDate() === selectedStartDate?.getDate()
                );
            }) ?? {};
        const extend = calendarDayPropMiddleware?.(dateItem);

        return {
            'aria-selected': isDaySelected,
            disabled:
                isExpired ||
                isMinimumRentalDuration ||
                isMaximumRentalDuration ||
                isInvalidCheckinDay ||
                isPreviousDayDisabled ||
                isInvalidCheckoutDay,
            'data-requirement-minimum-rental-duration': String(minDuration),
            'data-requirement-maximum-rental-duration': String(maxDuration),
            ...extend,
        };
    };

    return (
        <div className={className} data-testid="BasicCalendar">
            <div className="Calendar-headerTitle">
                {title && (
                    <span className="Calendar-headerTitleText" id="objectCalendarHeading">
                        {title}
                    </span>
                )}
                <button
                    type="button"
                    className="Calendar-resetButton bg-transparent"
                    onClick={handleReset}
                    data-testid="object-calendar-reset"
                    disabled={isResetButtonDisabled}>
                    Nullstill
                </button>
            </div>

            <div className="Calendar-nav">
                <NavBtn
                    direction={Direction.Left}
                    className="Calendar-navButton Calendar-navButton--left bg-transparent"
                    onClick={() => shiftDisplayMonth(-1)}
                    disabled={disallowedPreviousMonthDate.getTime() > selectedPreviousMonthDate.getTime()}
                    testId="BasicCalendar-prevMonthButton"
                />
                <p className="m-0">
                    {monthNames[monthIndex]} {year}
                </p>
                <NavBtn
                    direction={Direction.Right}
                    className="Calendar-navButton Calendar-navButton--right bg-transparent"
                    onClick={() => shiftDisplayMonth(1)}
                    disabled={disallowedFollowingMonthDate.getTime() < selectedNextMonthDate.getTime()}
                    testId="BasicCalendar-nextMonthButton"
                />
            </div>

            <Calendar
                createDayProps={createCalendarDayProps}
                dayDataByMonth={dayDataByMonth}
                events={updatedEvents}
                offsetStartDay={offsetStartDay}
                onDaySelect={handleDaySelect}
                activeMonthDate={activeMonthDate}
                today={today}
            />
        </div>
    );
};
